import {
  FilterLiveSearch,
  SavedQueriesList,
  FilterLiveForm,
  useGetList,
  FilterList,
  FilterListItem,
} from "react-admin";
import { Autocomplete, TextField, Stack } from "@mui/material";
import { useFormContext, Controller } from "react-hook-form";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { getResourceIcon } from "@/utils/icons";
import { ResourceName } from "@/types";
import { MultiSelectFilterList } from "@/components/common/MultiSelectFilterList";
import LinkIcon from "@mui/icons-material/Link";
import RuleIcon from "@mui/icons-material/Rule";
import { INELIGIBLE_REASON_FACET_OPTIONS } from "../ineligibleReasons";

const ClientFilterInput = () => {
  const { control, setValue } = useFormContext();

  const { data: clients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  return (
    <Controller
      name="client_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={clients || []}
          getOptionLabel={(option) => option.client_legal_name || ""}
          value={clients?.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
            setValue("claimant_id_ref", null);
            setValue("policy_id_ref", null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const ClaimantFilterInput = () => {
  const { control, watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  const { data: claimants, isLoading } = useGetList(ResourceName.CLAIMANTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "claimant_name", order: "ASC" },
    filter: selectedClientId ? { client_id_ref: selectedClientId } : {},
  });

  return (
    <Controller
      name="claimant_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={claimants || []}
          getOptionLabel={(option) => option.claimant_name || ""}
          value={claimants?.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Claimant" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const CascadingFilters = () => {
  const { watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  return (
    <Stack spacing={2}>
      <ClientFilterInput />
      {selectedClientId && <ClaimantFilterInput />}
    </Stack>
  );
};

export const ClaimItemListAside = () => {
  const ClaimItemIcon = getResourceIcon(ResourceName.CLAIM_ITEMS);

  return (
    <StyledListAside>
      <FilterLiveSearch />
      <SavedQueriesList />

      <FilterLiveForm>
        <CascadingFilters />
      </FilterLiveForm>

      {/* Multi-select status filter */}
      <MultiSelectFilterList
        source="claim_status"
        label="Status"
        icon={<ClaimItemIcon />}
        options={[
          { value: "submitted", label: "Submitted" },
          { value: "processing", label: "Processing" },
          { value: "approved", label: "Approved" },
          { value: "denied", label: "Denied" },
          { value: "paid", label: "Paid" },
        ]}
      />

      {/* Why a line is ineligible (#7380): the stamp's reason codes in plain language.
          Several codes are OR'd by the API (array overlap), so a reviewer can work the
          "Missing paid date" backlog on its own or with "Not paid". Exports follow the
          same filter through the list context. Sits right under Status because the
          two are worked together (walkthrough feedback 2026-10-26). */}
      <MultiSelectFilterList
        source="ineligible_reason"
        label="Reason"
        icon={<RuleIcon />}
        options={[...INELIGIBLE_REASON_FACET_OPTIONS]}
      />

      {/* Claim association filter */}
      <FilterList label="Claim Association" icon={<LinkIcon />}>
        <FilterListItem label="Linked to Claim" value={{ has_stoploss_claim: true }} />
        <FilterListItem label="Not Linked" value={{ has_stoploss_claim: false }} />
      </FilterList>

      {/* Eligibility filter */}
      <FilterList label="Eligibility" icon={<ClaimItemIcon />}>
        <FilterListItem label="Eligible" value={{ is_eligible: true }} />
        <FilterListItem label="Ineligible" value={{ is_eligible: false }} />
      </FilterList>
    </StyledListAside>
  );
};
